import { useState } from 'react'
import { Phone, Mail, MapPin, Clock, Send, Check } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { useRecaptcha } from '../hooks/useRecaptcha'
import { API_CONFIG } from '../config/api'
import { Helmet } from 'react-helmet-async'
import JsonLd from '../components/JsonLd'

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pax: '',
    dates: '',
    origin: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const { franchiseData, loading } = useFranchiseData()
  const { token: recaptchaToken, widgetRef: recaptchaWidgetRef, reset: resetRecaptcha, enabled: recaptchaEnabled } = useRecaptcha()

  // Helper function to determine if a number is a mobile number
  const isMobileNumber = (phone: string): boolean => {
    // Remove spaces, dashes, and other formatting
    const cleanPhone = phone.replace(/[\s\-()]/g, '')
    // Check if it's a South African mobile number (starts with +27 6/7/8/9 or 06/07/08/09)
    return /^(\+27|0)?[6-9]\d{8}$/.test(cleanPhone)
  }

  // Get WhatsApp number - prefer telephone if mobile, otherwise use emergency number
  const getWhatsAppNumber = (): string | null => {
    if (!franchiseData) return null
    
    if (franchiseData.telephone && isMobileNumber(franchiseData.telephone)) {
      return franchiseData.telephone.replace(/[\s\-()]/g, '')
    }
    
    if (franchiseData.emergency_telephone) {
      return franchiseData.emergency_telephone.replace(/[\s\-()]/g, '')
    }
    
    return null
  }

  const whatsappNumber = getWhatsAppNumber()

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    if (recaptchaEnabled && !recaptchaToken) {
      setSubmitError('Please complete the reCAPTCHA checkbox before submitting.')
      setIsSubmitting(false)
      return
    }

    try {
      // Construct the URL with query parameters
      const params = new URLSearchParams({
        'lead[first_name]': formData.firstName,
        'lead[last_name]': formData.lastName,
        'lead[email]': formData.email,
        'lead[telephone]': formData.phone,
        'lead[message]': formData.message,
        'lead[franchise_id]': API_CONFIG.FRANCHISE_ID || '135',
        'lead[pax]': formData.pax,
        'lead[dates]': formData.dates,
        'lead[origin]': formData.origin,
        ...(recaptchaToken ? { 'g-recaptcha-response': recaptchaToken } : {}),
      })

      const response = await fetch(`${API_CONFIG.BASE_URL}/leads?${params.toString()}`, {
        method: 'POST',
        headers: {
          'x-api-key': API_CONFIG.API_KEY,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        setSubmitSuccess(true)
        resetRecaptcha()
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          pax: '',
          dates: '',
          origin: '',
          message: '',
        })
        setTimeout(() => {
          setSubmitSuccess(false)
        }, 5000)
      } else {
        const errorData = await response.text()
        console.error('API Error:', response.status, errorData)
        throw new Error(`API Error: ${response.status}`)
      }
    } catch (error) {
      setSubmitError('Failed to send your message. Please try again.')
      console.error('Contact form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading || !franchiseData) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white py-24 sm:py-32">
      <Helmet>
        <title>Contact Us | Chelsea Travel</title>
        <meta
          name="description"
          content="Get in touch with Chelsea Travel’s expert consultants for tailored holiday planning and support."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/contact' : '/contact'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Us | Chelsea Travel" />
        <meta
          property="og:description"
          content="Speak to our travel experts and plan your perfect trip."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/contact'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact Us',
            url: typeof window !== 'undefined' ? window.location.href : '/contact',
            description:
              'Get in touch with Chelsea Travel’s expert consultants for tailored holiday planning and support.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Contact',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/contact',
              },
            ],
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Contact Our Travel Experts
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Ready to plan your dream vacation? Get in touch with our experienced travel consultants
            who will help create the perfect itinerary for you.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-16 lg:grid-cols-2">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Get in Touch</h3>

            <div className="space-y-6">
              {franchiseData?.telephone && (
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg"
                    style={{ background: '#58b8cd' }}
                  >
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Phone</h4>
                    <p className="mt-1 text-gray-600">
                      <a
                        href={`tel:${franchiseData.telephone.replace(/[^\d+]/g, '')}`}
                        className="hover:text-[#58b8cd]"
                      >
                        {franchiseData.telephone}
                      </a>
                    </p>
                    {franchiseData.emergency_telephone?.trim() && (
                      <p className="text-sm text-gray-500">
                        Emergency:{' '}
                        <a
                          href={`tel:${franchiseData.emergency_telephone.replace(/[^\d+]/g, '')}`}
                          className="hover:text-[#58b8cd]"
                        >
                          {franchiseData.emergency_telephone.trim()}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {franchiseData?.email && (
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg"
                    style={{ background: '#58b8cd' }}
                  >
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Email</h4>
                    <p className="mt-1 text-gray-600">
                      <a href={`mailto:${franchiseData.email}`} className="hover:text-[#58b8cd]">
                        {franchiseData.email}
                      </a>
                    </p>
                  </div>
                </div>
              )}

              {franchiseData?.address && (
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg"
                    style={{ background: '#58b8cd' }}
                  >
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Address</h4>
                    <p className="mt-1 text-gray-600">
                      {franchiseData.address
                        .split(/\r?\n/)
                        .map((line) => line.trim())
                        .filter(Boolean)
                        .map((line, index, lines) => (
                          <span key={index}>
                            {line}
                            {index < lines.length - 1 && <br />}
                          </span>
                        ))}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-4">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg"
                  style={{ background: '#58b8cd' }}
                >
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Working Hours</h4>
                  <div className="mt-1 text-gray-600">
                    <p>Monday - Friday: 8:30 AM - 5:00 PM</p>
                    <p>
                      Our office is open Tuesday to Thursday from 09h00 to 14h00 for consultations
                    </p>
                    {(franchiseData.telephone || franchiseData.email) && (
                      <p>
                        To contact a travel consultant, please
                        {franchiseData.telephone && (
                          <>
                            {' '}
                            call{' '}
                            <a
                              href={`tel:${franchiseData.telephone.replace(/[^\d+]/g, '')}`}
                              className="text-[#58b8cd] hover:underline"
                            >
                              {franchiseData.telephone}
                            </a>
                          </>
                        )}
                        {franchiseData.telephone && franchiseData.email && ' or'}
                        {franchiseData.email && (
                          <>
                            {' '}
                            email{' '}
                            <a
                              href={`mailto:${franchiseData.email}`}
                              className="text-[#58b8cd] hover:underline"
                            >
                              {franchiseData.email}
                            </a>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            {(franchiseData?.facebook || franchiseData?.instagram || franchiseData?.whatsapp) && (
              <div className="mt-12">
                <h4 className="font-medium text-gray-900 mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  {franchiseData.facebook && (
                    <a
                      href={franchiseData.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-blue-600"
                    >
                      Facebook
                    </a>
                  )}
                  {franchiseData.instagram && (
                    <a
                      href={franchiseData.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-pink-600"
                    >
                      Instagram
                    </a>
                  )}
                  {franchiseData.whatsapp && (
                    <a
                      href={`https://wa.me/${franchiseData.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-green-600"
                    >
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Contact Form */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Send us a Message</h3>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h4 className="text-lg font-medium text-green-600 mb-2">
                  Message Sent Successfully!
                </h4>
                <p className="text-gray-600">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-semibold text-gray-900"
                    >
                      First Name <span style={{ color: '#58b8cd' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#58b8cd] focus:border-[#58b8cd] transition-all duration-200 hover:border-gray-300"
                      placeholder="Enter your first name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="lastName" className="block text-sm font-semibold text-gray-900">
                      Last Name <span style={{ color: '#58b8cd' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#58b8cd] focus:border-[#58b8cd] transition-all duration-200 hover:border-gray-300"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900">
                    Email Address <span style={{ color: '#58b8cd' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#58b8cd] focus:border-[#58b8cd] transition-all duration-200 hover:border-gray-300"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900">
                    Phone Number <span style={{ color: '#58b8cd' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#58b8cd] focus:border-[#58b8cd] transition-all duration-200 hover:border-gray-300"
                    placeholder="+27 12 345 6789"
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="pax" className="block text-sm font-semibold text-gray-900">
                      Number of People <span style={{ color: '#58b8cd' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="pax"
                      id="pax"
                      required
                      value={formData.pax}
                      onChange={handleChange}
                      placeholder="e.g., 2 adults, 1 child"
                      className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#58b8cd] focus:border-[#58b8cd] transition-all duration-200 hover:border-gray-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="dates" className="block text-sm font-semibold text-gray-900">
                      Travel Dates <span style={{ color: '#58b8cd' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="dates"
                      id="dates"
                      required
                      value={formData.dates}
                      onChange={handleChange}
                      placeholder="e.g., December 2024"
                      className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#58b8cd] focus:border-[#58b8cd] transition-all duration-200 hover:border-gray-300"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="origin" className="block text-sm font-semibold text-gray-900">
                    Departure City <span style={{ color: '#58b8cd' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="origin"
                    id="origin"
                    required
                    value={formData.origin}
                    onChange={handleChange}
                    placeholder="e.g., Johannesburg, Cape Town"
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#58b8cd] focus:border-[#58b8cd] transition-all duration-200 hover:border-gray-300"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900">
                    Additional Message
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your travel preferences, special requirements, or any questions you have..."
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#58b8cd] focus:border-[#58b8cd] transition-all duration-200 hover:border-gray-300 resize-none"
                  />
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-red-600 text-sm font-medium">{submitError}</p>
                  </div>
                )}

                {recaptchaEnabled && (
                  <div className="flex justify-center">
                    <div ref={recaptchaWidgetRef}></div>
                  </div>
                )}

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 px-6 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center justify-center text-lg"
                    style={{
                      backgroundColor: franchiseData?.button_color || '#58b8cd',
                      borderColor: franchiseData?.button_color || '#58b8cd',
                    }}
                  >
                    <Send className="h-5 w-5 mr-3" />
                    {isSubmitting ? 'Sending Message...' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-24">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h3>

          <div className="mx-auto max-w-4xl">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  How far in advance should I book?
                </h4>
                <p className="text-gray-600 text-sm">
                  We recommend booking at least 2-3 months in advance for the best availability and
                  prices, especially for peak season travel. However, if your destination requires a
                  visa, you will probably need to book much longer in advance, as some countries are
                  experiencing backlogs in visa applications.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Do you offer travel insurance?</h4>
                <p className="text-gray-600 text-sm">
                  Yes, we can arrange comprehensive travel insurance to protect your trip investment
                  and provide peace of mind. Please note: You are responsible for reading the “fine
                  print” regarding the policy you choose.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Can you create custom packages?</h4>
                <p className="text-gray-600 text-sm">
                  Absolutely! Our travel experts specialize in creating personalized itineraries
                  tailored to your preferences and budget.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  What payment methods do you accept?
                </h4>
                <p className="text-gray-600 text-sm">We accept bank transfers and credit cards.</p>
              </div>
              <div className="text-center md:col-span-2 flex flex-col items-center justify-center">
                <h4 className="font-medium text-gray-900 mb-2">Do you help with visas?</h4>
                <p className="text-gray-600 text-sm">
                  Unfortunately, we are unable to assist with obtaining visas, due to the complex
                  process of navigating different immigration laws and regulations. However, we can
                  recommend a suitable visa service company{' '}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      {whatsappNumber && (
        <a
          href={`https://api.whatsapp.com/send?phone=${whatsappNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-8 right-8 z-50 flex h-16 w-16 items-center justify-center rounded-full shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-3xl"
          style={{ backgroundColor: '#25D366' }}
          aria-label="Chat on WhatsApp"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 175.216 175.552"
            className="h-10 w-10"
          >
            <defs>
              <linearGradient
                id="whatsapp-gradient"
                x1="85.915"
                x2="86.535"
                y1="32.567"
                y2="137.092"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#57d163" />
                <stop offset="1" stopColor="#23b33a" />
              </linearGradient>
            </defs>
            <path
              fill="url(#whatsapp-gradient)"
              d="M87.184 25.227c-33.733 0-61.166 27.423-61.178 61.13a60.98 60.98 0 0 0 9.349 32.535l1.455 2.312-6.179 22.559 23.146-6.069 2.235 1.324c9.387 5.571 20.15 8.518 31.126 8.524h.023c33.707 0 61.14-27.426 61.153-61.135a60.75 60.75 0 0 0-17.895-43.251 60.75 60.75 0 0 0-43.235-17.929z"
            />
            <path
              fill="#fff"
              d="M87.184 29.154c30.177 0 54.733 24.549 54.745 54.73a54.32 54.32 0 0 1-16.043 38.65 54.33 54.33 0 0 1-38.695 16.036h-.023c-9.582-.006-18.989-2.526-27.257-7.298l-31.052 8.143 8.289-30.265a54.57 54.57 0 0 1-7.868-28.04c.012-30.19 24.57-54.738 54.748-54.756z"
            />
            <path
              fill="#fff"
              fillRule="evenodd"
              d="M68.772 55.603c-1.378-3.061-2.828-3.123-4.137-3.176l-3.524-.043c-1.226 0-3.218.46-4.902 2.3s-6.435 6.287-6.435 15.332 6.588 17.785 7.506 19.013 12.718 20.381 31.405 27.75c15.529 6.124 18.689 4.906 22.061 4.6s10.877-4.447 12.408-8.74 1.532-7.971 1.073-8.74-1.685-1.226-3.525-2.146-10.877-5.367-12.562-5.981-2.91-.919-4.137.921-4.746 5.979-5.819 7.206-2.144 1.381-3.984.462-7.76-2.861-14.784-9.124c-5.465-4.873-9.154-10.891-10.228-12.73s-.114-2.835.808-3.751c.825-.824 1.838-2.147 2.759-3.22s1.224-1.84 1.836-3.065.307-2.301-.153-3.22-4.032-10.011-5.666-13.647"
            />
          </svg>
        </a>
      )}
    </div>
  )
}
